import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Learn.css";

/**
 * Learn — Full-Stack Roadmaps (Tree + Mind-Map hybrid)
 * - Searchable, collapsible lanes
 * - Each tech item links to its Q&A page
 * - Bootstrap 5 friendly
 */

const LANES = [
  {
    id: "foundations",
    title: "Foundations",
    emoji: "🧱",
    items: [
      { label: "Design Patterns", to: "/designPatterns" },
      { label: "Data Structures & Algorithms", to: "/dsa" },
      { label: "Behavioral", to: "/behavioral" }
    ]
  },
  {
    id: "frontend",
    title: "Frontend",
    emoji: "🎨",
    items: [
      { label: "JavaScript", to: "/javascript" },
      { label: "React", to: "/react" },
      { label: "Angular", to: "/angular" }
    ]
  },
  {
    id: "backend",
    title: "Backend",
    emoji: "🧠",
    items: [
      { label: "Java", to: "/java" },
      { label: "Spring", to: "/spring" },
      { label: "Hibernate", to: "/hibernate" },
      { label: "Python", to: "/python" },
      { label: "C#", to: "/cSharp" }
    ]
  },
  {
    id: "databases",
    title: "Databases",
    emoji: "🗄️",
    items: [
      { label: "SQL", to: "/sql" },
      { label: "MongoDB", to: "/mongoDB" },
      { label: "Cassandra", to: "/cassandra" },
      { label: "Elasticsearch", to: "/elasticSearch" }
    ]
  },
  {
    id: "data-messaging",
    title: "Data & Messaging",
    emoji: "📡",
    items: [{ label: "Kafka", to: "/kafka" }]
  },
  {
    id: "cloud-devops",
    title: "Cloud & DevOps",
    emoji: "☁️",
    items: [
      { label: "AWS", to: "/amazonWebServices" },
      { label: "Azure", to: "/azure" },
      { label: "Docker", to: "/docker" },
      { label: "Kubernetes", to: "/kubernetes" },
      { label: "Terraform", to: "/terraform" }
    ]
  },
  {
    id: "systems",
    title: "Architecture & Systems",
    emoji: "🏗️",
    items: [
      { label: "System Design", to: "/systemDesign" },
      { label: "Coding Round", to: "/codingRound" },
      { label: "Microservices", to: "/microservices" },
      { label: "REST APIs", to: "/restAPI" }
    ]
  }
];

const normalize = (s = "") => s.toLowerCase();

function Lane({ lane, onToggle, open, filter }) {
  // filter items by query (show lane if any match OR if query is empty)
  const items = useMemo(() => {
    if (!filter) return lane.items;
    return lane.items.filter((i) =>
      normalize(i.label).includes(normalize(filter))
    );
  }, [lane.items, filter]);

  // hide entire lane when searching and no matches
  if (filter && items.length === 0) return null;

  return (
    <section className="lane card shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="h6 text-uppercase text-muted mb-0">
            <span className="me-2">{lane.emoji}</span>
            {lane.title}
          </h3>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onToggle(lane.id)}
            aria-expanded={open}
            aria-controls={`lane-${lane.id}`}
          >
            {open ? "Collapse" : "Expand"}
          </button>
        </div>

        <div
          id={`lane-${lane.id}`}
          className={`lane-body ${open ? "open" : "closed"}`}
          role="region"
          aria-label={`${lane.title} items`}
        >
          <ul className="tree">
            {items.map((i, idx) => (
              <li className="tree-item" key={i.to}>
                <span className="branch" aria-hidden="true" />
                <Link to={i.to} className="chip" aria-label={`Go to ${i.label}`}>
                  <span className="dot" aria-hidden="true" />
                  <span className="chip-label">{i.label}</span>
                  <span className="arrow" aria-hidden="true">↗</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function QuickPaths() {
  const paths = [
    {
      title: "Full-Stack (Java)",
      steps: ["Java →", "Spring →", "Hibernate →", "REST APIs →", "SQL →", "Docker →", "Kubernetes"],
      links: ["/java", "/spring", "/hibernate", "/restAPI", "/sql", "/docker", "/kubernetes"]
    },
    {
      title: "Full-Stack (Python)",
      steps: ["Python →", "REST APIs →", "SQL/NoSQL →", "Docker →", "Kubernetes →", "System Design"],
      links: ["/python", "/restAPI", "/sql", "/docker", "/kubernetes", "/systemDesign"]
    },
    {
      title: "Frontend Power Path",
      steps: ["JavaScript →", "React →", "Angular →", "System Design (UI)"],
      links: ["/javascript", "/react", "/angular", "/systemDesign"]
    },
    {
      title: "Data & Search",
      steps: ["SQL →", "MongoDB →", "Cassandra →", "Elasticsearch →", "Kafka"],
      links: ["/sql", "/mongoDB", "/cassandra", "/elasticSearch", "/kafka"]
    }
  ];

  return (
    <section className="container py-4">
      <h2 className="h6 text-uppercase text-muted mb-3">Quick Start Paths</h2>
      <div className="row g-3">
        {paths.map((p) => (
          <div className="col-12 col-md-6 col-xl-3" key={p.title}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="fw-semibold mb-2">{p.title}</div>
                <div className="path">
                  {p.steps.map((s, idx) => (
                    <Link key={idx} to={p.links[idx]} className="path-step">
                      {s}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="card-footer bg-white border-0 small text-muted">
                Click any step to jump in
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Learn() {
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(new Set(LANES.map((l) => l.id))); // all open initially

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const shownCount = useMemo(() => {
    if (!query) return LANES.reduce((n, l) => n + l.items.length, 0);
    return LANES.reduce(
      (n, l) => n + l.items.filter((i) => normalize(i.label).includes(normalize(query))).length,
      0
    );
  }, [query]);

  return (
    <div className="learn-page">
      {/* Hero */}
      <section
        className="hero text-white text-center"
        role="banner"
        aria-label="Learn full stack development"
      >
        <div className="container py-5">
          <span className="badge bg-light text-dark mb-3">Roadmaps</span>
          <h1 className="display-6 fw-bold mb-2">Your Full-Stack Learning Map</h1>
          <p className="lead mb-4 opacity-90">
            Click through a visual roadmap for Backend, Frontend, Databases, DevOps, Cloud, and Architecture.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Link to="/systemDesign" className="btn btn-light btn-lg">System Design</Link>
            <Link to="/codingRound" className="btn btn-outline-light btn-lg">Practice Coding</Link>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="container control-strip">
        <div className="row g-2 align-items-center">
          <div className="col-12 col-md">
            <div className="input-group">
              <span className="input-group-text" aria-hidden="true">🔎</span>
              <input
                type="search"
                className="form-control"
                placeholder="Search tech (e.g., React, Spring, SQL, Docker)…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search technologies in roadmap"
              />
            </div>
            <div className="small text-muted mt-1">Showing {shownCount} items</div>
          </div>
          <div className="col-12 col-md-auto d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setOpenIds(new Set(LANES.map((l) => l.id)))}
            >
              Expand all
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setOpenIds(new Set())}
            >
              Collapse all
            </button>
          </div>
        </div>
      </section>

      {/* Tree lanes */}
      <section className="container py-3">
        <div className="row g-3">
          {LANES.map((lane) => (
            <div className="col-12 col-md-6 col-xl-4" key={lane.id}>
              <Lane
                lane={lane}
                filter={query}
                open={openIds.has(lane.id)}
                onToggle={toggle}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Quick paths (callouts) */}
      <QuickPaths />

      {/* Footer CTA */}
      <section className="cta text-center text-white">
        <div className="container py-5">
          <h2 className="fw-bold mb-2">Pick a lane and go build.</h2>
          <p className="text-white-50 mb-4">Interview-ready content with hands-on practice.</p>
          <div className="d-flex justify-content-center gap-2">
            <Link to="/java" className="btn btn-primary btn-lg">Backend (Java)</Link>
            <Link to="/react" className="btn btn-outline-light btn-lg">Frontend (React)</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
